import StatusChip from 'components/@extended/StatusChip';
import Loader from 'components/Loader';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import PartyName from 'components/party/PartyName';
import cloneDeep from 'lodash/cloneDeep';
import { HourlyRatesEditForm } from 'pages/pricelists/modal/HourlyRatesEditForm';
import { PricelistsCarousel } from 'pages/pricelists/modal/PricelistsCarousel';
import { useCallback, useMemo, useState } from 'react';
import { useGetJobRolesListQuery } from 'store/api/jobRoles';
import { ClassifierData } from 'types/classifiers';
import { JobRolesListElement } from 'types/jobRoles';
import { PricelistReference, UpdateHourlyRatesDto } from 'types/matters';
import { NamedEntityMinDto } from 'types/other';
import { HourlyRate, PricingKind, StagedPricelistPeriod } from 'types/pricelists';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { EmployeePricelistStartFrom } from './EmployeePricelistStartFrom';
import { EmployeeRatesEditForm } from './EmployeeRatesEditForm';
import {
    inForcePeriodOf,
    PeriodDraft,
    periodsFromList,
    stagedFromDrafts,
    validatePeriodDates,
} from './employeePricing';
import { useConfirmPastRepricing, useEmployeePeople, useToday } from './useEmployeePricing';

interface Props {
    clientParty?: NamedEntityMinDto;
    pricelistRates: HourlyRate[];
    pricelistReference: PricelistReference | null;
    onClose: () => void;
    onSubmit: (value: UpdateHourlyRatesDto) => void;
    classifiers: Record<string, ClassifierData>;
    currencyTypeCode?: string | null;
    serviceAgreementContractId?: number | null;
    // EMPLOYEE edits the list per colleague in periods and hands them back through onSubmitPeriods.
    pricingKind?: PricingKind;
    periods?: StagedPricelistPeriod[] | null;
    onSubmitPeriods?: (periods: StagedPricelistPeriod[]) => void;
}

const EMPTY_RATES: HourlyRate[] = [];

export function EditHourlyRatesModal(props: Props): JSX.Element {
    if (props.pricingKind === PricingKind.EMPLOYEE) {
        return (
            <EmployeeRatesModalBody
                pricelistId={props.pricelistReference?.id ?? undefined}
                periods={props.periods}
                currencyTypeCode={props.currencyTypeCode}
                onClose={props.onClose}
                onSubmit={(periods) => props.onSubmitPeriods?.(periods)}
            />
        );
    }
    return <RoleRatesModalBody {...props} />;
}

interface EmployeeBodyProps {
    pricelistId?: number;
    periods?: StagedPricelistPeriod[] | null;
    currencyTypeCode?: string | null;
    onClose: () => void;
    onSubmit: (periods: StagedPricelistPeriod[]) => void;
}

function EmployeeRatesModalBody({
    pricelistId,
    periods: staged,
    currencyTypeCode,
    onClose,
    onSubmit,
}: EmployeeBodyProps) {
    const today = useToday();
    const [initial] = useState(() => periodsFromList(staged));
    const [periods, setPeriods] = useState<PeriodDraft[]>(initial);
    const [activeKey, setActiveKey] = useState(() => inForcePeriodOf(initial, today)?.key ?? initial[0].key);
    const [isValidating, setIsValidating] = useState(false);
    // A list started from a model names the model's people, someone who has left among them.
    const [pricedPeriods, setPricedPeriods] = useState(staged);
    const { people, roleOrder, isLoading } = useEmployeePeople(pricedPeriods);
    const confirmPastRepricing = useConfirmPastRepricing(pricelistId);
    const dateErrors = useMemo(() => validatePeriodDates(periods), [periods]);

    const onStartFrom = (copied: StagedPricelistPeriod[]) => {
        const drafts = periodsFromList(copied);
        setPricedPeriods(copied);
        setPeriods(drafts);
        setActiveKey(inForcePeriodOf(drafts, today)?.key ?? drafts[0].key);
    };

    const onSave = () => {
        setIsValidating(true);
        const firstInvalid = periods.find((period) => dateErrors[period.key]);
        if (firstInvalid) {
            setActiveKey(firstInvalid.key);
            errorAlert(t('pricelists.employee.check-period-dates'));
            return;
        }
        void confirmPastRepricing(periods, initial, () => onSubmit(stagedFromDrafts(periods, people)));
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <>
            <ModalHeader title={t('pricelists.select-pricelist')} onCancel={onClose} />
            <div className="flex min-h-0 flex-1">
                <div className="flex w-5/12 flex-col gap-3 border-r border-zinc-300 px-4.5 pt-4">
                    <div>
                        <StatusChip type="neutral" label={t('pricelists.employee.per-employee')} />
                    </div>
                    <div className="text-grey-600 text-[0.8rem]">{t('pricelists.employee.intro')}</div>
                    {pricelistId ? null : (
                        <EmployeePricelistStartFrom currencyTypeCode={currencyTypeCode} onPick={onStartFrom} />
                    )}
                </div>
                <div className="h-[465px] min-w-0 flex-1 overflow-y-auto px-4.5 py-3.5">
                    <EmployeeRatesEditForm
                        periods={periods}
                        activeKey={activeKey}
                        onActiveKeyChange={setActiveKey}
                        onPeriodsChange={setPeriods}
                        people={people}
                        roleOrder={roleOrder}
                        dateErrors={dateErrors}
                        isValidating={isValidating}
                        today={today}
                    />
                </div>
            </div>
            <ModalFooter onCancel={onClose} onSave={onSave} saveButtonTextId="apply" />
        </>
    );
}

function RoleRatesModalBody({
    clientParty,
    pricelistReference,
    pricelistRates = EMPTY_RATES,
    onClose,
    onSubmit,
    classifiers,
    currencyTypeCode,
    serviceAgreementContractId,
}: Props): JSX.Element {
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [isValidating, setIsValidating] = useState(false);
    const { data: jobRoles, isLoading: isLoadingJobRoles } = useGetJobRolesListQuery();
    const hourlyRates: HourlyRate[] = useMemo(
        () =>
            (jobRoles || [])
                .filter((jobRole: JobRolesListElement) => {
                    const existingRate = pricelistRates?.find((rate) => rate.jobRole.id === jobRole.id);
                    const hasExistingValue = existingRate?.hourlyPrice && existingRate.hourlyPrice !== '';

                    // For new rates (no existing pricelistRates): only show active job roles
                    if (!pricelistRates || pricelistRates.length === 0) {
                        return jobRole.isActive;
                    }

                    // For existing rates: show active job roles OR inactive job roles that have existing values
                    return jobRole.isActive || hasExistingValue;
                })
                .map((jobRole: JobRolesListElement) => ({
                    jobRole,
                    hourlyPrice:
                        pricelistRates?.find((rate) => rate.jobRole.id === jobRole.id)?.hourlyPrice?.toString() ?? '',
                })),
        [jobRoles, pricelistRates]
    );

    const [formData, setFormData] = useState<UpdateHourlyRatesDto | null>(null);

    // Initialize form data once after async jobRoles load using a render-time guard
    // (https://react.dev/reference/react/useState#storing-information-from-previous-renders).
    // The `!formData` guard prevents infinite re-renders. Subsequent edits flow through
    // onHourlyRateChange / onPricelistChange.
    if (!isLoadingJobRoles && !formData) {
        setFormData({
            referencedPricelist: {
                id: pricelistReference?.id || null,
                pricingHash: pricelistReference?.pricingHash,
            },
            rates: hourlyRates,
        });
    }

    const [isApplying, setIsApplying] = useState(true);

    const onHourlyRateChange = useCallback(
        (value: HourlyRate[]) => {
            if (formData) {
                setIsApplying(false);
                const res = cloneDeep(formData);
                res['rates'] = value;
                setFormData(res);
            }
        },
        [formData]
    );

    const onPricelistChange = useCallback((value: UpdateHourlyRatesDto) => {
        setIsApplying(true);
        setFormData(value);
    }, []);

    const onSave = (): void => {
        if (formData) {
            setIsValidating(true);
            if (formData.rates.find((rate) => !rate.hourlyPrice && rate.jobRole.isActive)) {
                errorAlert(t('validation.required-fields-empty'));
                return;
            }

            onSubmit(formData);
        }
    };

    if (isLoadingJobRoles || !formData) {
        return <Loader />;
    }

    return (
        <>
            <ModalHeader title={t('pricelists.select-pricelist')} onCancel={onClose} />
            <div className="flex pl-[7px]">
                <div className="w-7/12 pt-4 pl-4">
                    <div className="h-[50px] pr-[23px]">
                        {t('pricelists.please-select-under-which-terms')}
                        {clientParty && (
                            <PartyName party={clientParty} variant="readonly-other" className="ml-1 font-medium" />
                        )}
                    </div>
                    <div className="h-[415px] overflow-y-auto pr-[23px]">
                        <PricelistsCarousel
                            pricelistData={formData}
                            onChange={onPricelistChange}
                            clientId={clientParty?.id}
                            currencyTypeCode={currencyTypeCode}
                            serviceAgreementContractId={serviceAgreementContractId}
                            setIsLoadingData={setIsLoadingData}
                        />
                    </div>
                </div>
                <div className="border-grey-300 w-5/12 border-l pt-4 pl-4">
                    <HourlyRatesEditForm
                        isValidating={isValidating}
                        hourlyRates={formData.rates}
                        onChange={onHourlyRateChange}
                        classifiers={classifiers}
                        height={465}
                        isLoading={isLoadingData}
                    />
                </div>
            </div>
            <ModalFooter onCancel={onClose} onSave={onSave} saveButtonTextId={isApplying ? 'apply' : 'save'} />
        </>
    );
}
